import { Controller } from "@hotwired/stimulus";

// The tag combobox (#59): filters the owner's vocabulary — handed to the
// section render as a value, refreshed with every stream replace — under
// the input. Picking a suggestion fills the input and submits the form
// right away (tagging is the only thing the input is for). Plain
// ArrowUp/Down + Enter + Escape keyboard support; no fetch, no debounce —
// the whole vocabulary is already here.
export default class extends Controller {
  static targets = ["input", "list"];
  static values = { vocabulary: Array };

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase();
    const matches = this.vocabularyValue
      .filter((name) => name.includes(query))
      .slice(0, 8);

    this.listTarget.replaceChildren(
      ...matches.map((name) => this.option(name)),
    );
    this.toggle(matches.length > 0);
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        this.highlight(event.key === "ArrowDown" ? 1 : -1);
        break;
      case "Enter": {
        const active = this.activeOption();
        if (active) {
          event.preventDefault();
          this.pick(active.dataset.name);
        }
        // No highlight: the form submits the typed value as-is.
        break;
      }
      case "Escape":
        this.toggle(false);
        break;
    }
  }

  // Blur fires before a click on an option lands — options commit on
  // pointerdown (see option()), so closing can safely wait a tick.
  closeLater() {
    setTimeout(() => this.toggle(false), 150);
  }

  option(name) {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("role", "option");
    button.dataset.name = name;
    button.textContent = name;
    button.className =
      "px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted aria-selected:bg-muted";
    button.setAttribute("aria-selected", "false");
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      this.pick(name);
    });
    return button;
  }

  pick(name) {
    this.inputTarget.value = name;
    this.toggle(false);
    this.inputTarget.form.requestSubmit();
  }

  highlight(direction) {
    if (this.listTarget.hidden) this.filter();
    const options = Array.from(this.listTarget.children);
    if (options.length === 0) return;

    const current = options.findIndex(
      (option) => option.getAttribute("aria-selected") === "true",
    );
    const next =
      (current + direction + options.length + 1) % (options.length + 1);
    options.forEach((option, index) => {
      option.setAttribute("aria-selected", index === next ? "true" : "false");
    });
    options[next]?.scrollIntoView({ block: "nearest" });
  }

  activeOption() {
    return this.listTarget.querySelector('[aria-selected="true"]');
  }

  toggle(open) {
    this.listTarget.hidden = !open;
    this.inputTarget.setAttribute("aria-expanded", open ? "true" : "false");
    if (!open) {
      for (const option of this.listTarget.children) {
        option.setAttribute("aria-selected", "false");
      }
    }
  }
}
